import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Search, Phone, MapPin, MessageSquare, ChevronDown, Loader2, ArrowRight, Plus, Building2, Bell, Trash2, Instagram, MessageCircle, Bot, Users } from 'lucide-react';
import { API_URL } from '../config';



const STATUSES = [
  { key: 'pleep_anel', label: { ru: 'Лиды Анель', kz: 'Анель лидтері', en: 'Anel Leads' }, color: '#a855f7', bg: '#faf5ff', border: '#e9d5ff', text: '#7e22ce' },
  { key: 'no_dealer', label: { ru: 'Без дилера', kz: 'Дилерсіз', en: 'No Dealer' }, color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', text: '#b45309' },
  { key: 'new', label: { ru: 'Новый', kz: 'Жаңа', en: 'New' }, color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
  { key: 'assigned_to_dealer', label: { ru: 'Передано дилеру', kz: 'Дилерге берілді', en: 'Assigned to Dealer' }, color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe', text: '#3730a3' },
  { key: 'in_progress', label: { ru: 'В работе', kz: 'Жұмыста', en: 'In Progress' }, color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe', text: '#4338ca' },
  { key: 'project_calc', label: { ru: 'Просчёт проекта', kz: 'Жоба есебі', en: 'Project Calc' }, color: '#a855f7', bg: '#faf5ff', border: '#e9d5ff', text: '#7e22ce' },
  { key: 'meeting', label: { ru: 'Встреча', kz: 'Кездесу', en: 'Meeting' }, color: '#0ea5e9', bg: '#f0f9ff', border: '#bae6fd', text: '#0369a1' },
  { key: 'kp_sent', label: { ru: 'КП отправлено', kz: 'КП жіберілді', en: 'Proposal Sent' }, color: '#f97316', bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
  { key: 'help_me', label: { ru: 'Help Me', kz: 'Help Me', en: 'Help Me' }, color: '#8b4444', bg: '#fdf2f2', border: '#e8b4b4', text: '#8b4444' },
  { key: 'approval', label: { ru: 'На согласовании', kz: 'Келісімде', en: 'Approval' }, color: '#eab308', bg: '#fefce8', border: '#fde68a', text: '#a16207' },
  { key: 'payment', label: { ru: 'Ожидание оплаты', kz: 'Төлем күтілуде', en: 'Awaiting Payment' }, color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', text: '#047857' },
  { key: 'completed', label: { ru: 'Оплачен', kz: 'Төленді', en: 'Paid' }, color: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
  { key: 'rejected', label: { ru: 'Отказ', kz: 'Бас тарту', en: 'Rejected' }, color: '#ef4444', bg: '#fef2f2', border: '#fecaca', text: '#b91c1c' },
];

const REJECTION_REASONS = [
  'Розница', 'Выбрали бамбук панели', 'Выбрали штукатурку', 'Дорого', 'Долго ждать',
  'Нет нужного цвета', 'Не актуально', 'Передумали',
  'Купили у конкурентов', 'Без ответа', 'Дорогая логистика', 'Просто интересовались',
];

// Pleep AI status → distinct color for the prominent bar on the lead card
// Available tags for manual lead tagging (marketing source tracking)
// Pleep leads CANNOT be tagged — they preserve their original state.
const LEAD_TAGS = ['Astanabuild 2026'];

// Company types for B2B segmentation
const COMPANY_TYPES = ['Дизайнер', 'Проектировщик', 'Застройщик', 'Архитектор', 'Заказчик', 'Субподрядчик', 'Агент', 'Магазин'];

const PLEEP_STATUS_COLORS = {
  'Без статуса': '#94a3b8',
  'Новый лид': '#3b82f6',
  'Сбор контактов': '#06b6d4',
  'Квалификация': '#8b5cf6',
  'Презентация': '#ec4899',
  'Передан менеджеру': '#f97316',
  'Дисквалифицирован': '#64748b',
  'Отказ клиента': '#ef4444',
};

const texts = {
  ru: { title: 'CRM — Заявки', search: 'Поиск по имени, телефону, городу...', noLeads: 'Нет заявок', all: 'Все', assignTo: 'Ответственный', assignDealer: 'Передать Дилеру', unassigned: 'Не назначен', notes: 'Заметки', assigned: 'Менеджер', dealer: 'Дилер', moveNext: 'Далее', movePrev: 'Назад' },
  kz: { title: 'CRM — Өтінімдер', search: 'Аты, телефоны бойынша іздеу...', noLeads: 'Өтінімдер жоқ', all: 'Барлығы', assignTo: 'Жауапты', assignDealer: 'Дилерге беру', unassigned: 'Тағайындалмаған', notes: 'Жазбалар', assigned: 'Менеджер', dealer: 'Дилер', moveNext: 'Келесі', movePrev: 'Артқа' },
  en: { title: 'CRM — Leads', search: 'Search by name, phone, city...', noLeads: 'No leads', all: 'All', assignTo: 'Responsible', assignDealer: 'Assign to Dealer', unassigned: 'Unassigned', notes: 'Notes', assigned: 'Manager', dealer: 'Dealer', moveNext: 'Next', movePrev: 'Back' },
};

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  let d = digits;
  if (d[0] === '8') d = '7' + d.slice(1);
  if (d[0] !== '7') d = '7' + d;
  d = d.slice(0, 11);
  let result = '+7';
  if (d.length > 1) result += ' (' + d.slice(1, 4);
  if (d.length >= 4) result += ') ';
  if (d.length > 4) result += d.slice(4, 7);
  if (d.length > 7) result += '-' + d.slice(7, 9);
  if (d.length > 9) result += '-' + d.slice(9, 11);
  return result;
};

const CRMPanel = ({ token, userRole }) => {
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;
  const isAdmin = userRole === 'admin';
  const isAdminOrManager = userRole === 'admin' || userRole === 'manager';
  const [leads, setLeads] = useState([]);
  const [managers, setManagers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedLead, setExpandedLead] = useState(null);
  const [editingNotes, setEditingNotes] = useState({});
  const [editingFields, setEditingFields] = useState({});
  const [savingNote, setSavingNote] = useState(null);
  const [rejectionFilter, setRejectionFilter] = useState('all');
  const [rejectionModal, setRejectionModal] = useState(null);
  const [dealerModal, setDealerModal] = useState(null);
  const [objectModal, setObjectModal] = useState(null);
  const [objectForm, setObjectForm] = useState({ name: '', address: '', area_m2: 0, lat: 51.1284, lng: 71.4306, description: '' });
  const [creatingObject, setCreatingObject] = useState(false);
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [showAddrSuggestions, setShowAddrSuggestions] = useState(false);
  const addrSuggestTimer = useRef(null);
  const addrGeocodeTimer = useRef(null);
  const [dragLead, setDragLead] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef(null);

  // Kanban click-and-drag panning (when grabbing empty space, not a lead card)
  const kanbanRef = useRef(null);
  const panStateRef = useRef(null); // { startX, startScrollLeft } while panning
  // Auto-scroll while dragging a lead near board edges
  const autoScrollTimerRef = useRef(null);
  const autoScrollDirRef = useRef(0); // -1 = left, 0 = idle, 1 = right

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Debounce search input — apply filter only 300ms after user stops typing
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [search]);

  // Stop kanban auto-scroll whenever the lead drag operation ends.
  useEffect(() => {
    if (!dragLead) {
      autoScrollDirRef.current = 0;
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current);
        autoScrollTimerRef.current = null;
      }
    }
    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current);
        autoScrollTimerRef.current = null;
      }
    };
  }, [dragLead]);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/leads`, { headers });
      if (res.ok) { setLeads(await res.json()); setFetchError(false); }
      else { setFetchError(true); }
    } catch (e) {
      console.error('fetchLeads error:', e);
      setFetchError(true);
    } finally { setLoading(false); }
  }, [token]);

  const fetchManagers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/leads/managers`, { headers });
      if (res.ok) setManagers(await res.json());
    } catch (e) { console.error('fetchManagers error:', e); }
  }, [token]);

  const fetchDealers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/leads/dealers`, { headers });
      if (res.ok) setDealers(await res.json());
    } catch (e) { console.error('fetchDealers error:', e); }
  }, [token]);

  useEffect(() => { fetchLeads(); fetchManagers(); fetchDealers(); }, [fetchLeads, fetchManagers, fetchDealers]);

  const updateLead = async (id, data) => {
    // Optimistic update — instant UI change
    setLeads(prev => prev.map(l => l.id === id ? { ...l, ...data } : l));
    try {
      await fetch(`${API_URL}/api/leads/${id}`, { method: 'PUT', headers, body: JSON.stringify(data) });
      // Silent background refresh to sync server-computed fields (dealer_name, assigned_to_name, etc.)
      const res = await fetch(`${API_URL}/api/leads`, { headers });
      if (res.ok) setLeads(await res.json());
    } catch (e) {
      console.error('updateLead error:', e);
      fetchLeads();
    }
  };

  const saveNotes = async (id) => {
    setSavingNote(id);
    await updateLead(id, { notes: editingNotes[id] || '' });
    setSavingNote(null);
  };

  const deleteLead = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/leads/${id}`, { method: 'DELETE', headers });
      if (!res.ok) {
        alert(language === 'ru' ? 'Не удалось удалить заявку. Проверьте права доступа.' : 'Failed to delete lead. Check permissions.');
        return;
      }
      setLeads(prev => prev.filter(l => l.id !== id));
      setExpandedLead(null);
    } catch {
      alert(language === 'ru' ? 'Ошибка сети при удалении' : 'Network error');
    }
  };

  const openObjectModal = (lead) => {
    setObjectForm({
      name: lead.object_name || '',
      address: lead.city || '',
      area_m2: lead.area_m2 || 0,
      lat: 51.1284, lng: 71.4306,
      description: '',
      deadline: lead.deal_deadline || '',
    });
    setObjectModal(lead.id);
  };

  const createObject = async () => {
    // Validate address is properly selected (not just partial text)
    if (!objectForm.name.trim() || objectForm.name.trim().length < 3) {
      alert(language === 'ru' ? 'Введите название объекта (минимум 3 символа)' : 'Enter object name (min 3 chars)');
      return;
    }
    if (!objectForm.address.trim() || objectForm.address.trim().length < 5) {
      alert(language === 'ru' ? 'Введите полный адрес объекта' : 'Enter full object address');
      return;
    }
    // Check address contains a house number (at least one digit)
    if (!/\d/.test(objectForm.address)) {
      alert(language === 'ru' ? 'Укажите полный адрес с номером дома (например: Астана, ул. Абая, 15)' : 'Enter full address with house number');
      return;
    }
    if (objectForm.lat === 51.1284 && objectForm.lng === 71.4306) {
      alert(language === 'ru' ? 'Выберите адрес из подсказок, чтобы координаты определились автоматически' : 'Select address from suggestions for automatic coordinates');
      return;
    }
    setCreatingObject(true);
    try {
      const res = await fetch(`${API_URL}/api/objects`, {
        method: 'POST', headers, body: JSON.stringify(objectForm)
      });
      if (res.ok) {
        await updateLead(objectModal, { object_name: objectForm.name });
        setObjectModal(null);
        alert(language === 'ru' ? `Объект "${objectForm.name}" создан и забронирован!` : `Object "${objectForm.name}" created and booked!`);
      } else {
        const err = await res.json();
        alert(err.detail || 'Error');
      }
    } catch (e) {
      console.error('createObject error:', e);
      alert(language === 'ru' ? 'Ошибка сети при создании объекта' : 'Network error');
    }
    setCreatingObject(false);
  };

  const handleObjectAddress = (value) => {
    setObjectForm(p => ({...p, address: value}));
    if (addrSuggestTimer.current) clearTimeout(addrSuggestTimer.current);
    if (addrGeocodeTimer.current) clearTimeout(addrGeocodeTimer.current);
    if (value.length < 3) { setAddrSuggestions([]); setShowAddrSuggestions(false); return; }
    addrSuggestTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/yandex/suggest?text=${encodeURIComponent(value)}&lang=${language}`, { headers });
        if (res.ok) {
          const data = await res.json();
          const items = (data.results || []).map(r => ({
            title: r.title?.text || '', subtitle: r.subtitle?.text || '',
            full_address: r.full_address || r.subtitle?.text || r.title?.text || '',
            lat: r.lat, lng: r.lng, need_geocode: r.need_geocode || false,
          }));
          setAddrSuggestions(items);
          setShowAddrSuggestions(items.length > 0);
        }
      } catch (e) { console.error('yandex/suggest error:', e); }
    }, 300);
    addrGeocodeTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/yandex/geocode?address=${encodeURIComponent(value)}`, { headers });
        if (res.ok) {
          const data = await res.json();
          if (data.lat && data.lng) setObjectForm(p => ({...p, lat: data.lat, lng: data.lng}));
        }
      } catch (e) { console.error('yandex/geocode error:', e); }
    }, 800);
  };

  const selectAddrSuggestion = async (s) => {
    const addr = s.full_address || `${s.title}, ${s.subtitle}`;
    setObjectForm(p => ({...p, address: addr}));
    setAddrSuggestions([]); setShowAddrSuggestions(false);
    if (s.lat && s.lng && !s.need_geocode) {
      setObjectForm(p => ({...p, lat: s.lat, lng: s.lng}));
    } else {
      try {
        const res = await fetch(`${API_URL}/api/yandex/geocode?address=${encodeURIComponent(addr)}`, { headers });
        if (res.ok) {
          const data = await res.json();
          if (data.lat && data.lng) setObjectForm(p => ({...p, lat: data.lat, lng: data.lng}));
        }
      } catch (e) { console.error('yandex/geocode (suggestion) error:', e); }
    }
  };

  const moveStatus = (lead, direction) => {
    const idx = STATUSES.findIndex(s => s.key === lead.status);
    const newIdx = idx + direction;
    if (newIdx >= 0 && newIdx < STATUSES.length) {
      updateLead(lead.id, { status: STATUSES[newIdx].key });
    }
  };

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newLead, setNewLead] = useState({ name: '', phone: '', city: '', area_m2: '', comment: '', tag: '', company_type: '', position: '' });
  const [creating, setCreating] = useState(false);

  const handleCreateLead = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const url = isAdmin ? `${API_URL}/api/leads` : `${API_URL}/api/leads/dealer`;
      await fetch(url, {
        method: 'POST', headers,
        body: JSON.stringify({ ...newLead, area_m2: parseFloat(newLead.area_m2) || 0, tag: newLead.tag || null, company_type: newLead.company_type || null, position: newLead.position || null }),
      });
      setShowCreateForm(false);
      setNewLead({ name: '', phone: '', city: '', area_m2: '', comment: '', tag: '', company_type: '', position: '' });
      fetchLeads();
    } catch (e) {
      console.error('handleCreateLead error:', e);
      alert(language === 'ru' ? 'Не удалось создать лида' : 'Failed to create lead');
    }
    setCreating(false);
  };

  const getStatus = (key) => STATUSES.find(s => s.key === key) || STATUSES[0];
  const getStatusIdx = (key) => STATUSES.findIndex(s => s.key === key);

  const filtered = leads.filter(l => {
    if (debouncedSearch) {
      const s = debouncedSearch.toLowerCase();
      return (l.name || '').toLowerCase().includes(s) || (l.phone || '').includes(s) || (l.city || '').toLowerCase().includes(s) || (l.assigned_to_name || '').toLowerCase().includes(s);
    }
    return true;
  });

  const counts = { all: leads.length };
  STATUSES.forEach(s => { counts[s.key] = leads.filter(l => l.status === s.key).length; });

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#8b4444]" /></div>;

  // ── Click-and-drag panning of the kanban board ──
  // Only fires when the user grabs *empty space* — not a lead card.
  const isInsideLeadCard = (target) => {
    let el = target;
    while (el && el !== kanbanRef.current) {
      if (el.dataset && (el.dataset.testid || '').startsWith('lead-')) return true;
      if (el.getAttribute && el.getAttribute('draggable') === 'true') return true;
      el = el.parentElement;
    }
    return false;
  };

  const handleBoardMouseDown = (e) => {
    if (e.button !== 0) return;
    if (isInsideLeadCard(e.target)) return;
    const board = kanbanRef.current;
    if (!board) return;
    panStateRef.current = { startX: e.clientX, startScrollLeft: board.scrollLeft };
    board.style.cursor = 'grabbing';
    board.style.userSelect = 'none';
  };

  const handleBoardMouseMove = (e) => {
    if (!panStateRef.current) return;
    const board = kanbanRef.current;
    if (!board) return;
    const dx = e.clientX - panStateRef.current.startX;
    board.scrollLeft = panStateRef.current.startScrollLeft - dx;
  };

  const handleBoardMouseUp = () => {
    if (!panStateRef.current) return;
    panStateRef.current = null;
    const board = kanbanRef.current;
    if (board) {
      board.style.cursor = '';
      board.style.userSelect = '';
    }
  };

  // ── Auto-scroll while dragging a lead near the left/right edges ──
  const startAutoScroll = (dir) => {
    if (autoScrollDirRef.current === dir) return;
    autoScrollDirRef.current = dir;
    if (autoScrollTimerRef.current) clearInterval(autoScrollTimerRef.current);
    if (dir === 0) return;
    autoScrollTimerRef.current = setInterval(() => {
      const board = kanbanRef.current;
      if (!board || autoScrollDirRef.current === 0) return;
      board.scrollLeft += autoScrollDirRef.current * 15;
    }, 50);
  };

  const stopAutoScroll = () => {
    autoScrollDirRef.current = 0;
    if (autoScrollTimerRef.current) {
      clearInterval(autoScrollTimerRef.current);
      autoScrollTimerRef.current = null;
    }
  };

  const handleBoardDragOver = (e) => {
    e.preventDefault(); // required to allow drop
    if (!dragLead) return;
    const board = kanbanRef.current;
    if (!board) return;
    const rect = board.getBoundingClientRect();
    const HOT = 120;
    if (e.clientX < rect.left + HOT) startAutoScroll(-1);
    else if (e.clientX > rect.right - HOT) startAutoScroll(1);
    else startAutoScroll(0);
  };

  return (
    <div className="space-y-4" data-testid="crm-panel">
      {fetchError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3" data-testid="crm-fetch-error">
          <p className="text-sm text-red-700 font-medium">
            {language === 'ru' ? 'Не удалось загрузить лиды. Попробуйте обновить страницу.' : 'Failed to load leads. Please refresh the page.'}
          </p>
          <button onClick={() => { setFetchError(false); fetchLeads(); }}
            className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700">
            {language === 'ru' ? 'Повторить' : 'Retry'}
          </button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#061d36]">{t.title}</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#061d36] font-medium">{leads.length} {language === 'ru' ? 'заявок' : 'leads'}</span>
          <button onClick={() => setShowCreateForm(!showCreateForm)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${showCreateForm ? 'bg-[#061d36]/10 text-[#061d36]' : 'bg-[#061d36] text-white hover:bg-[#0d2a47]'}`}
            data-testid="create-lead-btn">
            {showCreateForm ? (language === 'ru' ? 'Отмена' : 'Cancel') : (language === 'ru' ? 'Создать лида' : 'Create lead')}
          </button>
        </div>
      </div>

      {/* Create Lead Form */}
      {showCreateForm && (
        <form onSubmit={handleCreateLead} className="bg-white rounded-xl border border-[#8b4444]/20 p-5 space-y-3" data-testid="create-lead-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Название лида' : 'Lead name'} *</label>
              <input required value={newLead.name} onChange={e => setNewLead(p => ({...p, name: e.target.value}))}
                className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
            </div>
            <div>
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Телефон' : 'Phone'} *</label>
              <input required value={newLead.phone} onChange={e => setNewLead(p => ({...p, phone: formatPhone(e.target.value)}))}
                placeholder="+7 (___) ___-__-__"
                className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
            </div>
            <div>
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Город' : 'City'}</label>
              <input value={newLead.city} onChange={e => setNewLead(p => ({...p, city: e.target.value}))}
                className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
            </div>
          </div>
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Площадь стен (м²)' : 'Wall area (m²)'}</label>
              <input type="number" value={newLead.area_m2} onChange={e => setNewLead(p => ({...p, area_m2: e.target.value}))}
                className="w-full px-2 py-1.5 border border-[#061d36]/15 rounded-lg text-xs focus:outline-none focus:border-[#8b4444]" />
            </div>
            <div>
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Имя клиента' : 'Client name'}</label>
              <input value={newLead.client_name || ''} onChange={e => setNewLead(p => ({...p, client_name: e.target.value}))}
                placeholder={language === 'ru' ? 'ФИО клиента' : 'Full name'}
                className="w-full px-2 py-1.5 border border-[#061d36]/15 rounded-lg text-xs focus:outline-none focus:border-[#8b4444]" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{t.notes}</label>
              <input value={newLead.comment} onChange={e => setNewLead(p => ({...p, comment: e.target.value}))}
                className="w-full px-2 py-1.5 border border-[#061d36]/15 rounded-lg text-xs focus:outline-none focus:border-[#8b4444]" />
            </div>
          </div>
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Тег' : 'Tag'}</label>
              <select value={newLead.tag || ''} onChange={e => setNewLead(p => ({...p, tag: e.target.value}))}
                className="w-full px-2 py-1.5 border border-[#061d36]/15 rounded-lg text-xs bg-white focus:outline-none focus:border-[#8b4444]"
                data-testid="create-lead-tag-select">
                <option value="">{language === 'ru' ? '— Без тега —' : '— No tag —'}</option>
                {LEAD_TAGS.map(tg => <option key={tg} value={tg}>{tg}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Тип компании' : 'Company type'}</label>
              <select value={newLead.company_type || ''} onChange={e => setNewLead(p => ({...p, company_type: e.target.value}))}
                className="w-full px-2 py-1.5 border border-[#061d36]/15 rounded-lg text-xs bg-white focus:outline-none focus:border-[#8b4444]"
                data-testid="create-lead-company-type-select">
                <option value="">{language === 'ru' ? '— Не указан —' : '— Not specified —'}</option>
                {COMPANY_TYPES.map(ct => <option key={ct} value={ct}>{ct}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Должность' : 'Position'}</label>
              <input value={newLead.position || ''} onChange={e => setNewLead(p => ({...p, position: e.target.value}))}
                placeholder={language === 'ru' ? 'Например: Главный архитектор' : 'e.g. Chief architect'}
                className="w-full px-2 py-1.5 border border-[#061d36]/15 rounded-lg text-xs focus:outline-none focus:border-[#8b4444]"
                data-testid="create-lead-position-input" />
            </div>
          </div>
          <button type="submit" disabled={creating}
            className="px-6 py-2.5 bg-[#8b4444] text-white rounded-lg text-sm font-semibold hover:bg-[#7a3838] disabled:opacity-50"
            data-testid="create-lead-submit">
            {creating ? '...' : (language === 'ru' ? 'Создать лида' : 'Create lead')}
          </button>
        </form>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#061d36]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.search}
          className="w-full pl-9 pr-3 py-2 bg-white border border-[#061d36]/10 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]"
          data-testid="crm-search" />
      </div>

      {/* Empty state — when search yields no results */}
      {debouncedSearch && filtered.length === 0 && (
        <div className="text-center py-12 text-[#061d36]/40" data-testid="crm-empty-search">
          <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">{language === 'ru' ? 'Ничего не найдено' : 'Nothing found'}</p>
          <p className="text-xs mt-1">{language === 'ru' ? `По запросу «${debouncedSearch}»` : `For query "${debouncedSearch}"`}</p>
        </div>
      )}

      {/* Kanban Columns */}
      {!(debouncedSearch && filtered.length === 0) && (
      <div
        ref={kanbanRef}
        className="flex gap-3 overflow-x-auto pb-3 items-stretch kanban-scroll-x"
        style={{ height: '70vh', minHeight: '420px', maxHeight: 'calc(100vh - 220px)' }}
        onWheel={(e) => {
          // Forward ONLY horizontal trackpad gestures (2-finger swipe left/right).
          // Vertical wheel events are left untouched so list columns scroll natively.
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            e.currentTarget.scrollLeft += e.deltaX;
            e.preventDefault();
          }
        }}
        onMouseDown={handleBoardMouseDown}
        onMouseMove={handleBoardMouseMove}
        onMouseUp={handleBoardMouseUp}
        onMouseLeave={handleBoardMouseUp}
        onDragOver={handleBoardDragOver}
        onDragLeave={() => startAutoScroll(0)}
        onDrop={() => stopAutoScroll()}
        data-testid="crm-kanban-board"
      >
        {STATUSES.filter(col => (col.key !== 'help_me' || isAdmin) && (col.key !== 'pleep_anel' || isAdminOrManager) && (col.key !== 'no_dealer' || isAdminOrManager) && (col.key !== 'assigned_to_dealer' || isAdminOrManager)).map(col => {
          const colLeads = filtered.filter(l => l.status === col.key);
          const isDragTarget = dragOverCol === col.key;
          return (
            <div key={col.key}
              className={`shrink-0 w-[260px] h-full flex flex-col rounded-2xl transition-all duration-200 ${isDragTarget ? 'scale-[1.02] shadow-lg' : ''}`}
              style={{ backgroundColor: isDragTarget ? (col.key === 'help_me' ? '#6b3535' : '#0d2a47') : (col.key === 'help_me' ? '#8b4444' : '#061d36') }}
              onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
              onDrop={(e) => { e.preventDefault(); if (dragLead) { updateLead(dragLead, { status: col.key }); } setDragLead(null); setDragOverCol(null); }}
              onDragLeave={() => setDragOverCol(null)}
              data-testid={`crm-col-${col.key}`}>

              {/* Column header */}
              <div className="px-3 py-2.5 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />
                  <span className="text-[12px] font-bold text-white tracking-wide uppercase">{col.label[language]}</span>
                  {colLeads.length > 0 && <span className="text-[11px] text-white/30 font-medium">{colLeads.length}</span>}
                </div>
              </div>

              {/* Lead cards */}
              <div className="px-2 pb-2 space-y-2 overflow-y-auto flex-1 kanban-col-y">
                {colLeads.map(lead => (
                    <div key={lead.id}
                      draggable
                      onDragStart={(e) => { setDragLead(lead.id); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragEnd={() => { setDragLead(null); setDragOverCol(null); }}
                      className={`bg-white/95 rounded-xl transition-all duration-150 cursor-grab active:cursor-grabbing hover:bg-white hover:shadow-md border ${lead.priority ? 'border-l-[3px] border-l-[#8b4444] border-t-white/10 border-r-white/10 border-b-white/10' : 'border-white/10'} ${dragLead === lead.id ? 'opacity-30 scale-95' : 'shadow-sm'}`}
                      data-testid={`lead-${lead.id}`}>

                      {/* Tag bar — full-width gradient bar on top (above Pleep status, below name) */}
                      {lead.tag && (
                        <div className="flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold text-white rounded-t-xl bg-gradient-to-r from-amber-400 to-orange-500" data-testid="lead-tag-bar">
                          <span className="tracking-wide uppercase">{lead.tag}</span>
                        </div>
                      )}

                      {/* Pleep AI status bar — full-width colored top bar */}
                      {lead.pleep_status && (
                        <div className={`flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold text-white ${lead.tag ? '' : 'rounded-t-xl'}`}
                          style={{ backgroundColor: PLEEP_STATUS_COLORS[lead.pleep_status] || '#7c3aed' }}>
                          {lead.channel === 'instagram' ? <Instagram className="w-3 h-3" /> : lead.channel === 'whatsapp' ? <MessageCircle className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                          <span className="tracking-wide uppercase">Pleep · {lead.pleep_status}</span>
                        </div>
                      )}

                      {/* Compact card — click opens full modal */}
                      <div className="px-3 py-2.5 cursor-pointer" onClick={() => setExpandedLead(lead.id)}>
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex-1 min-w-0">
                            <span className="text-[13px] font-medium text-[#061d36] truncate block">{lead.name}</span>
                            {lead.client_name && <span className="text-[11px] text-[#061d36]/50 truncate block">{lead.client_name}</span>}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {lead.source === 'telegram' && (
                              <span className="text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold bg-[#229ED9] text-white">T</span>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); updateLead(lead.id, { priority: !lead.priority }); }}
                              className="w-5 h-5 flex items-center justify-center transition-all hover:scale-110">
                              <img src="/logo-icon-dark.png" alt="" className="w-3.5 h-3.5 transition-all"
                                style={lead.priority
                                  ? { filter: 'brightness(0) saturate(100%) invert(25%) sepia(50%) saturate(900%) hue-rotate(320deg) brightness(0.7)' }
                                  : { opacity: 0.12 }
                                } />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-[#061d36]/40 flex-wrap">
                          {lead.phone && <span className="flex items-center gap-0.5 whitespace-nowrap"><Phone className="w-2.5 h-2.5" />{formatPhone(lead.phone)}</span>}
                          {lead.city && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{lead.city}</span>}
                          {lead.area_m2 > 0 && <span className="font-semibold text-[#061d36]/50">{lead.area_m2} м²</span>}
                          {lead.deal_amount > 0 && <span className="font-bold text-emerald-600">{Number(lead.deal_amount).toLocaleString()} ₸</span>}
                          {lead.dealer_name && isAdminOrManager && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium whitespace-nowrap"
                              data-testid={`lead-dealer-badge-${lead.id}`}
                              title={`Передано дилеру: ${lead.dealer_name}`}
                            >
                              <Users className="w-2.5 h-2.5" />
                              {lead.dealer_name}
                            </span>
                          )}
                        </div>
                        {(lead.object_name || lead.dealer_name || lead.assigned_to_name) && (
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            {lead.object_name && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#061d36]/5 text-[#061d36]/50 font-medium truncate max-w-[140px]"><Building2 className="w-2.5 h-2.5 inline mr-0.5" />{lead.object_name}</span>
                            )}
                            {lead.dealer_name && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#8b4444]/10 text-[#8b4444] font-medium" data-testid="lead-dealer-preview">{lead.dealer_name}</span>
                            )}
                            {lead.assigned_to_name && !lead.dealer_name && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-700 font-semibold flex items-center gap-0.5" data-testid="lead-assignee-preview">
                                <Users className="w-2.5 h-2.5" />{lead.assigned_to_name}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Quick notes — always visible */}
                      <div className="px-2.5 pb-2" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <input
                            value={editingNotes[lead.id] !== undefined ? editingNotes[lead.id] : (lead.notes || lead.comment || '')}
                            onChange={e => setEditingNotes(p => ({...p, [lead.id]: e.target.value}))}
                            onKeyDown={e => { if (e.key === 'Enter') saveNotes(lead.id); }}
                            placeholder={language === 'ru' ? 'Заметка...' : 'Note...'}
                            className="flex-1 min-w-0 px-2 py-1 bg-[#061d36]/[0.03] border border-transparent rounded text-[10px] text-[#061d36]/60 focus:outline-none focus:border-[#8b4444]/30 focus:bg-white" />
                          {editingNotes[lead.id] !== undefined && editingNotes[lead.id] !== (lead.notes || lead.comment || '') && (
                            <button onClick={() => saveNotes(lead.id)} disabled={savingNote === lead.id}
                              className="px-1.5 py-1 bg-[#061d36] text-white rounded text-[9px] font-medium hover:bg-[#0d2a47] disabled:opacity-50">
                              {savingNote === lead.id ? '...' : 'OK'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                ))}
                {colLeads.length === 0 && !search && (
                  <div className="px-3 pb-3">
                    <span className="text-[11px] text-white/15">{language === 'ru' ? 'Пусто' : 'Empty'}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Full Lead Detail Modal */}
      {expandedLead && (() => {
        const lead = leads.find(l => l.id === expandedLead);
        if (!lead) return null;
        const st = getStatus(lead.status);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            onMouseDown={e => { if (e.target === e.currentTarget) setExpandedLead(null); }}>
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="p-5 border-b border-[#061d36]/10 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <input value={editingFields[lead.id]?.name !== undefined ? editingFields[lead.id].name : (lead.name || '')}
                    onChange={e => setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), name: e.target.value}}))}
                    onBlur={() => { const v = editingFields[lead.id]?.name; if (v !== undefined && v !== (lead.name || '')) updateLead(lead.id, { name: v }); }}
                    className="text-xl font-bold text-[#061d36] bg-transparent border-none focus:outline-none focus:bg-[#061d36]/[0.02] rounded px-1 -ml-1 w-full" />
                  <p className="text-xs text-[#061d36]/40 mt-0.5">{new Date(lead.created_at).toLocaleString()} {lead.source === 'telegram' && '(Telegram)'}</p>
                </div>
                {(lead.object_name || true) && (
                  <div className="flex items-center gap-1.5 flex-shrink-0 bg-[#061d36]/5 rounded-lg px-2 py-1.5">
                    <Building2 className="w-3.5 h-3.5 text-[#061d36]/40 flex-shrink-0" />
                    <input value={editingFields[lead.id]?.object_name !== undefined ? editingFields[lead.id].object_name : (lead.object_name || '')}
                      onChange={e => setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), object_name: e.target.value}}))}
                      onBlur={() => { const v = editingFields[lead.id]?.object_name; if (v !== undefined && v !== (lead.object_name || '')) updateLead(lead.id, { object_name: v }); }}
                      placeholder={language === 'ru' ? 'Объект...' : 'Object...'}
                      className="text-xs font-semibold text-[#061d36]/60 bg-transparent border-none focus:outline-none w-[130px]" />
                  </div>
                )}
                <button onClick={() => { if (window.confirm(language === 'ru' ? 'Удалить заявку?' : 'Delete lead?')) deleteLead(lead.id); }}
                  className="text-[#061d36]/20 hover:text-red-500 transition-colors flex-shrink-0" data-testid="delete-lead-btn"
                  title={language === 'ru' ? 'Удалить' : 'Delete'}>
                  <Trash2 className="w-5 h-5" />
                </button>
                <button onClick={() => setExpandedLead(null)} className="text-[#061d36]/30 hover:text-[#061d36] transition-colors flex-shrink-0">
                  <span className="text-2xl leading-none">&times;</span>
                </button>
              </div>

              <div className="p-5 space-y-4">
                {/* Status pills */}
                <div className="flex flex-wrap gap-1.5">
                  {STATUSES.map(s => (
                    <button key={s.key} onClick={() => { if (s.key === 'rejected') setRejectionModal(lead.id); else updateLead(lead.id, { status: s.key }); }}
                      className="text-[11px] font-semibold transition-all px-3 py-1 rounded-full border"
                      style={lead.status === s.key
                        ? { color: 'white', backgroundColor: s.color, borderColor: s.color }
                        : { color: '#061d36', backgroundColor: 'transparent', borderColor: '#061d3620' }
                      }>{s.label[language]}</button>
                  ))}
                </div>

                {lead.status === 'rejected' && lead.rejection_reason && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#061d36]/50">{language === 'ru' ? 'Причина:' : 'Reason:'}</span>
                    <span className="px-2.5 py-1 rounded text-[11px] font-medium bg-[#061d36] text-white">{lead.rejection_reason}</span>
                    <button onClick={() => setRejectionModal(lead.id)} className="text-[11px] text-[#8b4444] underline">{language === 'ru' ? 'изменить' : 'change'}</button>
                  </div>
                )}

                {/* Tag selector — disabled for Pleep leads (preserve original state) */}
                {(() => {
                  const isPleepLead = (lead.source || '').startsWith('pleep_');
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-[#061d36]/60 font-semibold">{language === 'ru' ? 'Тег:' : 'Tag:'}</span>
                      {isPleepLead ? (
                        lead.tag ? (
                          <span className="text-[11px] px-2.5 py-1 rounded bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold uppercase tracking-wide">{lead.tag}</span>
                        ) : (
                          <span className="text-[11px] text-[#061d36]/40 italic">{language === 'ru' ? '— не редактируется для Pleep лидов —' : '— not editable for Pleep leads —'}</span>
                        )
                      ) : (
                        <select
                          value={lead.tag || ''}
                          onChange={e => updateLead(lead.id, { tag: e.target.value })}
                          className="text-xs px-2 py-1 border border-[#061d36]/20 rounded bg-white focus:outline-none focus:border-[#8b4444]"
                          data-testid="lead-tag-select"
                        >
                          <option value="">{language === 'ru' ? '— Без тега —' : '— No tag —'}</option>
                          {LEAD_TAGS.map(tg => <option key={tg} value={tg}>{tg}</option>)}
                        </select>
                      )}
                    </div>
                  );
                })()}

                {/* Pleep AI bot info */}
                {lead.pleep_status && (
                  <div className="bg-[#7c3aed]/5 border border-[#7c3aed]/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Bot className="w-4 h-4 text-[#7c3aed]" />
                      <span className="text-xs font-bold text-[#7c3aed] uppercase tracking-wide">Pleep AI</span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-[#061d36]/80 items-center">
                      <label className="inline-flex items-center gap-1">
                        <strong>{language === 'ru' ? 'Статус Pleep:' : 'Pleep status:'}</strong>
                        <select
                          value={lead.pleep_status}
                          onChange={e => updateLead(lead.id, { pleep_status: e.target.value })}
                          className="text-xs px-2 py-1 border border-[#7c3aed]/30 rounded bg-white focus:outline-none focus:border-[#7c3aed]"
                          data-testid="pleep-status-select"
                        >
                          {['Без статуса','Новый лид','Сбор контактов','Квалификация','Презентация','Передан менеджеру','Дисквалифицирован','Отказ клиента'].map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </label>
                      {lead.channel && (
                        <span className="inline-flex items-center gap-1">
                          {lead.channel === 'instagram' ? <Instagram className="w-3 h-3" /> : <MessageCircle className="w-3 h-3" />}
                          <strong>{language === 'ru' ? 'Канал:' : 'Channel:'}</strong> {lead.channel}
                        </span>
                      )}
                      {lead.instagram_handle && (
                        <a href={`https://instagram.com/${(lead.instagram_handle || '').replace('@','')}`} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gradient-to-r from-[#f09433] to-[#bc1888] text-white font-semibold hover:opacity-90"
                           data-testid="pleep-instagram-link">
                          <Instagram className="w-3 h-3" /> {lead.instagram_handle}
                        </a>
                      )}
                      {lead.phone && (
                        <a href={`https://wa.me/${(lead.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#25D366] text-white font-semibold hover:bg-[#20BD5A]"
                           data-testid="pleep-whatsapp-link">
                          <MessageCircle className="w-3 h-3" /> WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Main fields */}
                <div className="grid sm:grid-cols-4 gap-3">
                  <div>
                    <p className="text-xs text-[#061d36] font-semibold mb-1">{language === 'ru' ? 'Имя' : 'Name'}</p>
                    <input value={editingFields[lead.id]?.client_name !== undefined ? editingFields[lead.id].client_name : (lead.client_name || '')}
                      onChange={e => setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), client_name: e.target.value}}))}
                      onBlur={() => { const v = editingFields[lead.id]?.client_name; if (v !== undefined && v !== (lead.client_name || '')) updateLead(lead.id, { client_name: v }); }}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]"
                      placeholder={language === 'ru' ? 'Имя' : 'Name'} />
                  </div>
                  <div>
                    <p className="text-xs text-[#061d36] font-semibold mb-1">{language === 'ru' ? 'Телефон' : 'Phone'}</p>
                    <input value={formatPhone(editingFields[lead.id]?.phone !== undefined ? editingFields[lead.id].phone : (lead.phone || ''))}
                      onChange={e => { const formatted = formatPhone(e.target.value); setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), phone: formatted}})); }}
                      onBlur={() => { const v = editingFields[lead.id]?.phone; if (v !== undefined && v !== (lead.phone || '')) updateLead(lead.id, { phone: v }); }}
                      placeholder="+7 (___) ___-__-__"
                      className="w-full px-2.5 py-2 border border-[#061d36]/15 rounded-lg text-[13px] focus:outline-none focus:border-[#8b4444]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#061d36] font-semibold mb-1">{language === 'ru' ? 'Город' : 'City'}</p>
                    <input value={editingFields[lead.id]?.city !== undefined ? editingFields[lead.id].city : (lead.city || '')}
                      onChange={e => setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), city: e.target.value}}))}
                      onBlur={() => { const v = editingFields[lead.id]?.city; if (v !== undefined && v !== (lead.city || '')) updateLead(lead.id, { city: v }); }}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#061d36] font-semibold mb-1">м²</p>
                    <input type="number" value={editingFields[lead.id]?.area_m2 !== undefined ? editingFields[lead.id].area_m2 : (lead.area_m2 || '')}
                      onChange={e => setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), area_m2: e.target.value}}))}
                      onBlur={() => { const v = editingFields[lead.id]?.area_m2; if (v !== undefined && v !== String(lead.area_m2 || '')) updateLead(lead.id, { area_m2: parseFloat(v) || 0 }); }}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
                  </div>
                </div>

                {/* Object & Dates */}
                <div className="grid sm:grid-cols-4 gap-3">
                  <div>
                    <p className="text-xs text-[#061d36] font-semibold mb-1">{language === 'ru' ? 'Объект' : 'Object'}</p>
                    <input value={editingFields[lead.id]?.object_name !== undefined ? editingFields[lead.id].object_name : (lead.object_name || '')}
                      onChange={e => setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), object_name: e.target.value}}))}
                      onBlur={() => { const v = editingFields[lead.id]?.object_name; if (v !== undefined && v !== (lead.object_name || '')) updateLead(lead.id, { object_name: v }); }}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#061d36] font-semibold mb-1">{language === 'ru' ? 'Дата потребности' : 'Need date'}</p>
                    <input type="date" value={editingFields[lead.id]?.need_date !== undefined ? editingFields[lead.id].need_date : (lead.need_date || '')}
                      onChange={e => { setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), need_date: e.target.value}})); updateLead(lead.id, { need_date: e.target.value }); }}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#061d36] font-semibold mb-1">{language === 'ru' ? 'Дедлайн сделки' : 'Deadline'}</p>
                    <input type="date" value={editingFields[lead.id]?.deal_deadline !== undefined ? editingFields[lead.id].deal_deadline : (lead.deal_deadline || '')}
                      onChange={e => { setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), deal_deadline: e.target.value}})); updateLead(lead.id, { deal_deadline: e.target.value }); }}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#061d36] font-semibold mb-1">{language === 'ru' ? 'Сумма сделки (₸)' : 'Deal amount (₸)'}</p>
                    <input type="number" value={editingFields[lead.id]?.deal_amount !== undefined ? editingFields[lead.id].deal_amount : (lead.deal_amount || '')}
                      onChange={e => setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), deal_amount: e.target.value}}))}
                      onBlur={() => { const v = editingFields[lead.id]?.deal_amount; if (v !== undefined && v !== String(lead.deal_amount || '')) updateLead(lead.id, { deal_amount: parseFloat(v) || 0 }); }}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#061d36] font-semibold mb-1">{language === 'ru' ? 'Тип компании' : 'Company type'}</p>
                    <select value={lead.company_type || ''}
                      onChange={e => updateLead(lead.id, { company_type: e.target.value })}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm bg-white focus:outline-none focus:border-[#8b4444]"
                      data-testid="lead-company-type-select">
                      <option value="">{language === 'ru' ? '— Не указан —' : '— Not specified —'}</option>
                      {COMPANY_TYPES.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs text-[#061d36] font-semibold mb-1">{language === 'ru' ? 'Должность' : 'Position'}</p>
                    <input value={editingFields[lead.id]?.position !== undefined ? editingFields[lead.id].position : (lead.position || '')}
                      onChange={e => setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), position: e.target.value}}))}
                      onBlur={() => { const v = editingFields[lead.id]?.position; if (v !== undefined && v !== (lead.position || '')) updateLead(lead.id, { position: v }); }}
                      placeholder={language === 'ru' ? 'Например: Главный архитектор' : 'e.g. Chief architect'}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]"
                      data-testid="lead-position-input" />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <p className="text-xs text-[#061d36] font-semibold mb-1">{t.notes}</p>
                  <textarea
                    value={editingNotes[lead.id] !== undefined ? editingNotes[lead.id] : (lead.notes || lead.comment || '')}
                    onChange={e => setEditingNotes(p => ({...p, [lead.id]: e.target.value}))}
                    rows={6}
                    className="w-full min-h-[160px] px-3 py-2 border border-[#061d36]/10 rounded-lg text-sm focus:outline-none focus:border-[#8b4444] resize-y"
                    placeholder={language === 'ru' ? 'Добавить заметку...' : 'Add note...'} />
                  {editingNotes[lead.id] !== undefined && editingNotes[lead.id] !== (lead.notes || lead.comment || '') && (
                    <button onClick={() => saveNotes(lead.id)} disabled={savingNote === lead.id}
                      className="mt-1 px-3 py-1 bg-[#061d36] text-white rounded-lg text-xs font-medium hover:bg-[#0d2a47] disabled:opacity-50">
                      {savingNote === lead.id ? '...' : (language === 'ru' ? 'Сохранить' : 'Save')}
                    </button>
                  )}
                </div>

                {/* Responsible — admin only */}
                {isAdmin && (
                <div>
                  <p className="text-xs text-[#061d36] font-semibold mb-1.5">{t.assignTo}</p>
                  <select value={lead.assigned_to || ''}
                    onChange={e => updateLead(lead.id, { assigned_to: e.target.value })}
                    className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444] bg-white">
                    <option value="">{t.unassigned}</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.company_name}</option>)}
                  </select>
                </div>
                )}

                {/* Actions + Reminder */}
                <div className="flex items-center gap-2 flex-wrap">
                  {isAdmin && (
                  <button onClick={() => setDealerModal(lead.id)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all border-2 border-[#8b4444] text-[#8b4444] hover:bg-[#8b4444] hover:text-white">
                    {t.assignDealer} <ArrowRight className="w-3 h-3" />
                  </button>
                  )}
                  {lead.dealer_name && (
                    <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-[#8b4444]/10 text-[#061d36] border border-[#8b4444]/30">{lead.dealer_name}</span>
                  )}
                  <button onClick={() => openObjectModal(lead)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                    <Building2 className="w-3 h-3" />{language === 'ru' ? 'Забронировать объект' : 'Book Object'}
                  </button>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <Bell className="w-3.5 h-3.5 text-[#8b4444]" />
                    <input type="datetime-local"
                      value={editingFields[lead.id]?.reminder_at !== undefined ? editingFields[lead.id].reminder_at : (lead.reminder_at || '')}
                      onChange={e => { const v = e.target.value; setEditingFields(p => ({...p, [lead.id]: {...(p[lead.id] || {}), reminder_at: v}})); updateLead(lead.id, { reminder_at: v }); }}
                      className="px-2 py-1 border border-[#8b4444]/20 rounded-lg text-xs focus:outline-none focus:border-[#8b4444] max-w-[200px]" />
                    {lead.reminder_at && (
                      <button onClick={() => updateLead(lead.id, { reminder_at: '' })}
                        className="text-[10px] text-[#8b4444] hover:underline">{language === 'ru' ? 'X' : 'X'}</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Rejection Reason Modal */}
      {rejectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" onClick={() => setRejectionModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#061d36] mb-4">{language === 'ru' ? 'Причина отказа' : 'Rejection reason'}</h3>
            <div className="grid grid-cols-2 gap-2">
              {REJECTION_REASONS.map(reason => (
                <button key={reason} onClick={() => {
                  updateLead(rejectionModal, { status: 'rejected', rejection_reason: reason });
                  setRejectionModal(null);
                }}
                  className="px-3 py-2.5 rounded-xl text-sm font-medium transition-all border-2 bg-white text-[#061d36] border-[#061d36]/20 hover:border-[#061d36] hover:bg-[#061d36] hover:text-white text-left">
                  {reason}
                </button>
              ))}
            </div>
            <button onClick={() => setRejectionModal(null)}
              className="mt-4 w-full py-2 text-sm text-[#061d36]/50 hover:text-[#061d36] transition-colors">
              {language === 'ru' ? 'Отмена' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Dealer Assignment Modal */}
      {dealerModal && (() => {
        const lead = leads.find(l => l.id === dealerModal);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" onClick={() => setDealerModal(null)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-[#061d36] mb-1">{t.assignDealer}</h3>
              {lead && <p className="text-sm text-[#061d36]/50 mb-4">{lead.client_name} — {lead.phone}</p>}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => { updateLead(dealerModal, { dealer_id: '' }); setDealerModal(null); }}
                  data-testid="dealer-modal-unassigned"
                  className={`px-3 py-3 rounded-xl text-sm font-medium transition-all border-2 text-left ${!lead?.dealer_id ? 'bg-[#8b4444] text-white border-[#8b4444] font-bold' : 'bg-white text-[#061d36] border-[#061d36]/20 hover:border-[#8b4444] hover:bg-[#8b4444]/5'}`}>
                  {t.unassigned}
                </button>
                {dealers.map(d => (
                  <button key={d.id} onClick={() => { updateLead(dealerModal, { dealer_id: d.id }); setDealerModal(null); }}
                    data-testid={`dealer-modal-option-${d.id}`}
                    className={`px-3 py-3 rounded-xl text-sm font-medium transition-all border-2 text-left ${lead?.dealer_id === d.id ? 'bg-[#8b4444]/10 text-[#061d36] border-[#8b4444] font-bold' : 'bg-white text-[#061d36] border-[#061d36]/20 hover:border-[#8b4444] hover:bg-[#8b4444]/5'}`}>
                    {d.short_name || d.company_name}
                  </button>
                ))}
              </div>
              <button onClick={() => setDealerModal(null)}
                className="mt-4 w-full py-2 text-sm text-[#061d36]/50 hover:text-[#061d36] transition-colors">
                {language === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Object Booking Modal */}
      {objectModal && (() => {
        const lead = leads.find(l => l.id === objectModal);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" onClick={() => { setObjectModal(null); setShowAddrSuggestions(false); }}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-[#061d36] mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600" />
                {language === 'ru' ? 'Создать и забронировать объект' : 'Create & Book Object'}
              </h3>

              {/* Lead info — readonly */}
              {lead && (
                <div className="flex items-center gap-3 mb-4 px-3 py-2 bg-[#061d36]/[0.03] rounded-lg border border-[#061d36]/10">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#061d36] truncate">{lead.client_name || lead.name}</p>
                    <p className="text-xs text-[#061d36]/50">{lead.city}{lead.area_m2 > 0 ? ` · ${lead.area_m2} м²` : ''}</p>
                  </div>
                  <a href={`tel:${lead.phone}`} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200 hover:bg-emerald-100 transition-colors flex-shrink-0">
                    <Phone className="w-3 h-3" />{formatPhone(lead.phone)}
                  </a>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Название объекта' : 'Object name'} *</label>
                  <input value={objectForm.name} onChange={e => setObjectForm(p => ({...p, name: e.target.value}))}
                    className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
                    placeholder={language === 'ru' ? 'ЖК Премиум / Офис на Абая' : 'Object name'} />
                </div>
                <div className="relative">
                  <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Адрес' : 'Address'} *</label>
                  <input value={objectForm.address} onChange={e => handleObjectAddress(e.target.value)}
                    onFocus={() => { if (addrSuggestions.length > 0) setShowAddrSuggestions(true); }}
                    className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
                    placeholder={language === 'ru' ? 'Начните вводить адрес...' : 'Start typing address...'} />
                  {showAddrSuggestions && addrSuggestions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#061d36]/15 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                      {addrSuggestions.map((s, i) => (
                        <button key={i} onClick={() => selectAddrSuggestion(s)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-[#061d36]/5 last:border-b-0 transition-colors">
                          <span className="font-medium text-[#061d36]">{s.title}</span>
                          {s.subtitle && <span className="text-[#061d36]/50 ml-1 text-xs">{s.subtitle}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Площадь (м²)' : 'Area (m²)'}</label>
                    <input type="number" value={objectForm.area_m2 || ''} onChange={e => setObjectForm(p => ({...p, area_m2: parseFloat(e.target.value) || 0}))}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Дата реализации' : 'Deadline'}</label>
                    <input type="date" value={objectForm.deadline || ''} onChange={e => setObjectForm(p => ({...p, deadline: e.target.value}))}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="text-xs text-[#061d36] font-semibold mb-1 block">{language === 'ru' ? 'Описание' : 'Description'}</label>
                    <input value={objectForm.description} onChange={e => setObjectForm(p => ({...p, description: e.target.value}))}
                      className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-emerald-500" />
                  </div>
                </div>
                <p className="text-[10px] text-[#061d36]/40">{language === 'ru' ? 'Координаты' : 'Coordinates'}: {objectForm.lat.toFixed(4)}, {objectForm.lng.toFixed(4)} — {language === 'ru' ? 'уточняются автоматически по адресу' : 'auto-detected from address'}</p>
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={() => { setObjectModal(null); setShowAddrSuggestions(false); }}
                  className="flex-1 py-2.5 text-sm text-[#061d36]/50 hover:text-[#061d36] transition-colors border border-[#061d36]/10 rounded-lg">
                  {language === 'ru' ? 'Отмена' : 'Cancel'}
                </button>
                <button onClick={createObject} disabled={creatingObject || !objectForm.name || !objectForm.address}
                  data-testid="create-object-submit"
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-all">
                  {creatingObject ? '...' : (language === 'ru' ? 'Создать и забронировать' : 'Create & Book')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CRMPanel;
